import { Theme } from "@mui/material";
import { SystemStyleObject } from "@mui/system";
import { LINE, LINE_STRONG, MUTED, TEAL, TEXT } from "../../vegEditor/editorTheme";

export {
  FAINT, LINE, LINE_STRONG, MUTED, TEAL, TEAL_INK, TEXT,
  btnGhost, btnPlain, btnPrimary, btnSmall, capSx, kbdSx, menuItemSx,
} from "../../vegEditor/editorTheme";

type Sx = SystemStyleObject<Theme>;

/** The configuration modal's own surfaces. */
export const BG = "#1a1d2e";
export const PANEL = "#20233a";
export const PANEL_HEAD = "#252838";
export const INSET = "rgba(0,0,0,0.22)";
export const MENU_BG = "#252838";

/** Edited (blue), invalid (red) and warning (amber) tones. */
export const BLUE = "#5aa9ec";
export const BLUE_TEXT = "#a9cdfd";
export const RED = "#ef5350";
export const RED_TEXT = "#ff9e9a";
export const AMBER_TEXT = "#f5ca76";

export const panelSx: Sx = { bgcolor: PANEL, border: `1px solid ${LINE}`, borderRadius: 3 };

export const labelSx: Sx = { fontSize: 12, fontWeight: 500, color: MUTED };

export type ChipTone = "mod" | "new" | "del" | "warn" | "plain";

export const chipSx = (tone: ChipTone): Sx => ({
  display: "inline-flex", alignItems: "center", flex: "none", height: 22, px: 1, borderRadius: 11,
  fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
  bgcolor: {
    mod: "rgba(90,169,236,0.18)", new: "rgba(0,212,173,0.16)", del: "rgba(239,83,80,0.18)",
    warn: "rgba(245,166,35,0.16)", plain: "rgba(255,255,255,0.08)",
  }[tone],
  color: { mod: BLUE_TEXT, new: "#7aebd2", del: RED_TEXT, warn: AMBER_TEXT, plain: "rgba(255,255,255,0.8)" }[tone],
});

/** An outlined field (text input or select) at 36px, marked when edited or invalid. */
export const fieldSx = (state: { mod?: boolean; err?: boolean } = {}): Sx => ({
  fontSize: 13, color: TEXT, borderRadius: 2,
  bgcolor: state.err ? "rgba(239,83,80,0.07)" : state.mod ? "rgba(90,169,236,0.08)" : INSET,
  ".MuiOutlinedInput-notchedOutline": { borderColor: state.err ? RED : state.mod ? "rgba(90,169,236,0.6)" : LINE_STRONG },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: state.err ? RED : "rgba(255,255,255,0.3)" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: TEAL, borderWidth: 1 },
  "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(0,212,173,0.16)" },
  ".MuiSvgIcon-root": { color: MUTED },
  ".MuiInputBase-input": { py: "8.5px" },
  "&.Mui-disabled": { opacity: 0.5 },
  ".MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "rgba(255,255,255,0.6)" },
});

/** A table cell input: no border until hovered or focused. */
export const cellSx = (state: { mod?: boolean; err?: boolean; num?: boolean; struck?: boolean } = {}): Sx => ({
  width: "100%", height: 34, fontSize: 13, color: state.mod ? "#d6e7fd" : TEXT, borderRadius: 1.75,
  border: `1px solid ${state.err ? RED : "transparent"}`,
  bgcolor: state.err ? "rgba(239,83,80,0.09)" : state.mod ? "rgba(90,169,236,0.13)" : "transparent",
  "&:hover": { borderColor: state.err ? RED : LINE_STRONG, bgcolor: state.err || state.mod ? undefined : INSET },
  "&.Mui-focused": { borderColor: TEAL, bgcolor: INSET, boxShadow: "0 0 0 3px rgba(0,212,173,0.16)" },
  "& input": { px: 1.25, textAlign: state.num ? "right" : "left", fontVariantNumeric: "tabular-nums", textDecoration: state.struck ? "line-through" : "none" },
  ".MuiSelect-select": { pl: 1.25, pr: "26px !important", py: 0, lineHeight: "32px", textDecoration: state.struck ? "line-through" : "none" },
  ".MuiSvgIcon-root": { color: MUTED, right: 4 },
  "&.Mui-disabled": { "&:hover": { borderColor: "transparent", bgcolor: "transparent" } },
  ".Mui-disabled": { WebkitTextFillColor: "rgba(255,255,255,0.55)" },
});

export const checkboxSx: Sx = { p: 0.75, color: "rgba(255,255,255,0.45)", "&.Mui-checked": { color: TEAL } };

export const sliderSx: Sx = {
  py: 1,
  "& .MuiSlider-track": { bgcolor: TEAL, borderColor: TEAL },
  "& .MuiSlider-thumb": { bgcolor: TEAL, width: 14, height: 14 },
  "& .MuiSlider-rail": { bgcolor: "rgba(255,255,255,0.15)" },
};

export const iconBtnSx: Sx = {
  width: 32, height: 32, borderRadius: 1.75, color: MUTED,
  "&:hover": { bgcolor: "rgba(255,255,255,0.08)", color: TEXT },
  "&.Mui-disabled": { color: "rgba(255,255,255,0.25)" },
};

export const linkBtnSx: Sx = {
  p: 0, minWidth: 0, fontSize: 12, fontWeight: 500, textTransform: "none", color: TEAL, textDecoration: "underline",
  textUnderlineOffset: 2, verticalAlign: "baseline", "&:hover": { bgcolor: "transparent", color: "#5ee6c8", textDecoration: "underline" },
};

export const menuPaperSx: Sx = { bgcolor: MENU_BG, color: TEXT, border: `1px solid ${LINE_STRONG}`, maxHeight: 420 };
